/**
 * useInviteTeam — wraps the deployed provision-users Edge Function.
 *
 * Body shape mirrors the Edge Function's validated schema:
 *   { emails: string[], role?: 'cleaner' | 'supervisor', organization_id?: string }
 *
 * The Edge Function returns { provisioned: string[], failed: [...] }.
 * We forward that result verbatim so the page can render the per-email
 * outcomes; transport-level errors (FORBIDDEN_ROLE, RATE_LIMITED, etc.)
 * map to AppError.userMessage like the rest of the app.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export type InviteRole = 'cleaner' | 'supervisor'

export interface InviteTeamVars {
  emails: string[]
  role:   InviteRole
}

export interface InviteTeamFailure {
  email:   string
  code:    string
  message: string
}

export interface InviteTeamResult {
  provisioned: string[]
  failed:      InviteTeamFailure[]
}

export function useInviteTeam(): UseMutationResult<InviteTeamResult, AppError, InviteTeamVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<InviteTeamResult, AppError, InviteTeamVars>({
    mutationFn: async (vars): Promise<InviteTeamResult> => {
      const { data, error } = await supabase.functions.invoke<InviteTeamResult>('provision-users', {
        body: {
          emails: vars.emails,
          role:   vars.role,
        },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'INVITE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (data) => {
      // Any successfully provisioned email creates a new profile row
      // via the handle_new_user trigger. Invalidate the team list +
      // the cleaner picker on create-task so both surfaces refresh.
      if (data.provisioned.length > 0) {
        void queryClient.invalidateQueries({ queryKey: ['org-team']     })
        void queryClient.invalidateQueries({ queryKey: ['org-cleaners'] })
      }
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_ROLE':              return 'Pick a valid role (cleaner or supervisor).'
    case 'FORBIDDEN_ROLE_ESCALATION': return 'Only the platform owner can create contractor admins.'
    case 'FORBIDDEN_ROLE':            return 'Your role is not allowed to invite team members.'
    case 'NO_ORG_CONTEXT':            return 'Your account is not attached to an organization.'
    case 'CROSS_ORG_FORBIDDEN':       return 'You can only invite people into your own organization.'
    case 'TOO_MANY_EMAILS':           return 'Send no more than 50 invites in a single batch.'
    case 'INVALID_EMAIL':             return 'One of the addresses is not a valid email.'
    case 'INVALID_BODY':              return 'The invite request was malformed.'
    case 'RATE_LIMITED':              return 'Too many invites in a row. Pause for a moment.'
    case 'ORG_REQUIRED':              return 'organization_id is required for this role.'
    default:                          return detail || 'Could not send the invites.'
  }
}
